import {
  FormItem,
  FormControl,
  Button,
  NumberInput,
} from '@/shared/components';
import { getRequiredLabel } from '@/shared/lib';
import { cn } from '@/shared/utils';
import type { ControllerFieldState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AssetOption, AvailableParams } from '../model';
import React from 'react';

interface InternalTransferAmountFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  value: string | number;
  fieldState: ControllerFieldState;
  isAmountDisabled: boolean;
  selectedAssetFrom?: AssetOption;
  onMaxClick: () => void;
  availablePlaceholder: string;
  availableParams?: AvailableParams;
  exchangeRateDescription: string;
}

const InternalTransferAmountField = React.forwardRef<
  HTMLInputElement,
  InternalTransferAmountFieldProps
>(
  (
    {
      fieldState,
      isAmountDisabled,
      selectedAssetFrom,
      onMaxClick,
      availablePlaceholder,
      availableParams,
      exchangeRateDescription,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();

    const handleChange = React.useCallback(
      (newValue: string) => {
        const syntheticEvent = {
          target: { value: newValue },
          currentTarget: { value: newValue },
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        onChange?.(syntheticEvent);
      },
      [onChange]
    );

    const placeholder = t('common.amountPlaceholder');

    return (
      <FormItem className="flex flex-col gap-[0.4rem]">
        <FormControl>
          <NumberInput
            {...props}
            isCustomElement
            ref={ref}
            value={value}
            type="text"
            inputMode="decimal"
            label={getRequiredLabel(placeholder)}
            onChange={e => handleChange(e.target.value)}
            decimalScale={selectedAssetFrom?.precision}
            className="p3 rounded-[1.6rem]"
            disabled={isAmountDisabled}
            rightContent={
              selectedAssetFrom && (
                <Button
                  type="button"
                  size="icon-button"
                  variant="icon"
                  onClick={onMaxClick}>
                  <span className="headline5 text-uppercase">
                    {t('withdrawMethods.crypto.maxButton')}
                  </span>
                </Button>
              )
            }
          />
        </FormControl>
        {selectedAssetFrom && (
          <div
            className={cn(
              'px-[1.6rem] text-muted-foreground p6',
              fieldState.error && 'text-destructive'
            )}>
            {t(availablePlaceholder, availableParams)}
          </div>
        )}
        {exchangeRateDescription && (
          <div className="px-[1.6rem] text-muted-foreground p6">
            {t('exchange.exchangeRate')}: {exchangeRateDescription}
          </div>
        )}
      </FormItem>
    );
  }
);

InternalTransferAmountField.displayName = 'InternalTransferAmountField';

export { InternalTransferAmountField };
